import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CompanyRole,
  companyRoleSchema,
  createCompanyRoleSchema,
  createsLoop,
  type OrganizationUser,
  PERSON_KINDS,
  type PersonKind,
  type RolePerson,
} from '@k-management/shared';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Field } from '../../components/Field.js';
import { Icon } from '../../components/Icon.js';
import { apiRequest } from '../../lib/api.js';
import { type FieldErrors, showServerError, validate } from '../../lib/form-validation.js';
import { queryKeys } from '../../lib/query-keys.js';

/** A person while being chosen: a user of K-Management, or a name for who has no access. */
interface PersonDraft {
  userId: string | null;
  name: string;
  kind: PersonKind;
  /** Written by hand: a person without access. */
  text: boolean;
}

interface FormState {
  code: string;
  title: string;
  parent: string;
  holders: PersonDraft[];
  substitute: PersonDraft;
  notes: string;
}

const EMPTY: PersonDraft = { userId: null, name: '', kind: 'INT', text: false };
const TEXT = '__text';

function draftOf(person: RolePerson | null): PersonDraft {
  if (!person) return EMPTY;
  return {
    userId: person.userId ?? null,
    name: person.name,
    kind: person.kind,
    text: !person.userId,
  };
}

/** The person to send, or null when nothing was chosen. */
function personOf(draft: PersonDraft, users: OrganizationUser[]): RolePerson | null {
  if (draft.userId) {
    const user = users.find((item) => item.id === draft.userId);
    return { name: user?.name ?? draft.name, kind: 'INT', userId: draft.userId };
  }
  return draft.text && draft.name.trim()
    ? { name: draft.name.trim(), kind: draft.kind, userId: null }
    : null;
}

const KIND_LABELS: Record<PersonKind, string> = { INT: 'Interno', EST: 'Esterno' };

/** Add or change a company role: title, place in the hierarchy, people, notes. */
export function RoleDialog({
  role,
  roles,
  onClose,
}: {
  role: CompanyRole | null;
  roles: CompanyRole[];
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const queryClient = useQueryClient();
  const users = useQuery({
    queryKey: ['organization', 'users'],
    queryFn: () => apiRequest<OrganizationUser[]>('/organization/users'),
  });
  const people = users.data ?? [];
  // The roles of the rules take users only (decision of 04/10/2026).
  const usersOnly = role?.locked ?? false;
  const [form, setForm] = useState<FormState>(() => ({
    code: '',
    title: role?.title ?? '',
    parent: role?.parent ?? '',
    holders: role?.holders.length ? role.holders.map(draftOf) : [EMPTY],
    substitute: draftOf(role?.substitute ?? null),
    notes: role?.notes ?? '',
  }));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.companyRoles });

  const save = useMutation({
    mutationFn: (body: unknown) =>
      role
        ? apiRequest<CompanyRole>(`/organization/roles/${role.code}`, { method: 'PUT', body })
        : apiRequest<CompanyRole>('/organization/roles', { method: 'POST', body }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
    onError: (error) => showServerError(error, setErrors, setFormError),
  });

  const remove = useMutation({
    mutationFn: () => apiRequest<void>(`/organization/roles/${role!.code}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });

  // A role cannot report to itself, nor to a role below it.
  const parentOptions = roles.filter(
    (item) => !role || (item.code !== role.code && !createsLoop(roles, role.code, item.code)),
  );

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const setHolder = (index: number, person: PersonDraft) =>
    set(
      'holders',
      form.holders.map((item, at) => (at === index ? person : item)),
    );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const body = {
      ...(role ? {} : { code: form.code }),
      title: form.title,
      parent: form.parent || null,
      holders: form.holders.flatMap((draft) => personOf(draft, people) ?? []),
      substitute: personOf(form.substitute, people),
      notes: form.notes,
    };
    const result = role
      ? validate(companyRoleSchema, body)
      : validate(createCompanyRoleSchema, body);
    setErrors(result.errors);
    if (Object.keys(result.errors).length === 0) save.mutate(body);
  };

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <form onSubmit={submit} className="stack" noValidate>
        <h2>{role ? role.title : 'Nuovo ruolo'}</h2>

        <div className="form-grid">
          <Field label="Ruolo" error={errors.title}>
            <input value={form.title} onChange={(event) => set('title', event.target.value)} />
          </Field>
          {!role && (
            <Field
              label="Codice"
              error={errors.code}
              hint="Facoltativo: se lo lasci vuoto lo assegna il programma"
            >
              <input value={form.code} onChange={(event) => set('code', event.target.value)} />
            </Field>
          )}
          <Field label="Riporta a" error={errors.parent}>
            <select value={form.parent} onChange={(event) => set('parent', event.target.value)}>
              <option value="">Nessuno: è in cima</option>
              {parentOptions.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.title}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <fieldset className="stack tight">
          <legend>Assegnato a</legend>
          {usersOnly && (
            <p className="muted small">
              Questo ruolo lo usano le regole: si sceglie tra gli utenti di K-Management.
            </p>
          )}
          {form.holders.map((person, index) => (
            <div key={index} className="org-person-row">
              <PersonPicker
                label={`Persona ${index + 1}`}
                value={person}
                users={people}
                usersOnly={usersOnly}
                onChange={(next) => setHolder(index, next)}
              />
              <button
                type="button"
                className="icon-button"
                aria-label="Togli"
                title="Togli"
                onClick={() =>
                  set(
                    'holders',
                    form.holders.filter((_, at) => at !== index),
                  )
                }
              >
                <Icon name="close" size={16} />
              </button>
              {errors[`holders.${index}.name`] && (
                <span className="field-error">{errors[`holders.${index}.name`]}</span>
              )}
            </div>
          ))}
          <div>
            <button
              type="button"
              className="link"
              onClick={() => set('holders', [...form.holders, EMPTY])}
            >
              + Aggiungi persona
            </button>
          </div>
        </fieldset>

        <fieldset className="stack tight">
          <legend>Sostituto</legend>
          <div className="org-person-row">
            <PersonPicker
              label="Sostituto"
              value={form.substitute}
              users={people}
              usersOnly={usersOnly}
              empty="Nessuno"
              onChange={(next) => set('substitute', next)}
            />
          </div>
        </fieldset>

        <Field label="Note" error={errors.notes}>
          <input value={form.notes} onChange={(event) => set('notes', event.target.value)} />
        </Field>

        {role?.locked && (
          <p className="muted small">
            Ruolo usato dalle regole: si cambia chi lo ricopre, ma non si elimina.
          </p>
        )}
        {formError && <p className="error">{formError}</p>}

        <div className="actions">
          {role && !role.locked && (
            <button
              type="button"
              className="danger org-delete"
              onClick={() => {
                remove.reset();
                setConfirmDelete(true);
              }}
            >
              Elimina
            </button>
          )}
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button type="submit" className="primary" disabled={save.isPending}>
            {save.isPending ? 'Salvataggio…' : 'Salva'}
          </button>
        </div>
      </form>

      {confirmDelete && role && (
        <ConfirmDialog
          title="Eliminare il ruolo?"
          confirmLabel="Elimina"
          busy={remove.isPending}
          error={remove.error?.message ?? null}
          onConfirm={() => remove.mutate()}
          onCancel={() => setConfirmDelete(false)}
        >
          <p>
            «{role.title}» esce dall’organigramma. Resta nello storico della tabella Ruoli
            aziendali.
          </p>
        </ConfirmDialog>
      )}
    </dialog>
  );
}

/**
 * Who holds a role: a user of K-Management from the list, or, where allowed,
 * a person without access written by hand with «interno» or «esterno».
 */
function PersonPicker({
  label,
  value,
  users,
  usersOnly,
  empty = 'Scegli…',
  onChange,
}: {
  label: string;
  value: PersonDraft;
  users: OrganizationUser[];
  usersOnly: boolean;
  empty?: string;
  onChange: (person: PersonDraft) => void;
}) {
  const choice = value.userId ?? (value.text ? TEXT : '');
  return (
    <>
      <select
        aria-label={label}
        value={choice}
        onChange={(event) => {
          const next = event.target.value;
          if (next === TEXT) onChange({ ...EMPTY, text: true });
          else if (next === '') onChange(EMPTY);
          else {
            const user = users.find((item) => item.id === next);
            onChange({ userId: next, name: user?.name ?? '', kind: 'INT', text: false });
          }
        }}
      >
        <option value="">{empty}</option>
        {users.map((user) => (
          <option key={user.id} value={user.id}>
            {user.name} · {user.email}
          </option>
        ))}
        {/* Someone no longer active stays visible until changed. */}
        {value.userId && !users.some((user) => user.id === value.userId) && (
          <option value={value.userId}>{value.name}</option>
        )}
        {(!usersOnly || value.text) && <option value={TEXT}>Persona senza accesso…</option>}
      </select>
      {value.text && (
        <>
          <input
            aria-label={`${label}: nome`}
            placeholder="Nome e cognome, o società"
            value={value.name}
            onChange={(event) => onChange({ ...value, name: event.target.value })}
          />
          <KindSelect value={value.kind} onChange={(kind) => onChange({ ...value, kind })} />
        </>
      )}
    </>
  );
}

function KindSelect({
  value,
  onChange,
}: {
  value: PersonKind;
  onChange: (kind: PersonKind) => void;
}) {
  return (
    <select
      aria-label="Interno o esterno"
      value={value}
      onChange={(event) => onChange(event.target.value as PersonKind)}
    >
      {PERSON_KINDS.map((kind) => (
        <option key={kind} value={kind}>
          {KIND_LABELS[kind]}
        </option>
      ))}
    </select>
  );
}
